import { Klarna3x } from "@/components/shop/PayLogos";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Minus, Plus, Star, Truck, ShieldCheck, RefreshCw, Gem, ChevronRight, Facebook, Link2, Share2, Check, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, eur, errMsg, img, srcset, track } from "@/lib/util";
import { gaItem } from "@/lib/ga";
import { useStore } from "@/context/Store";

export const Price = ({ p, size = "base" }) => (
  <div className={`flex items-baseline gap-2 ${size === "lg" ? "text-2xl" : "text-sm"}`} data-testid="price-display">
    <span className={p.on_sale ? "text-[#9A6B12] font-semibold" : "font-medium"}>{eur(p.price)}</span>
    {p.on_sale && <span className="line-through text-black/40 text-[0.85em]">{eur(p.compare_at)}</span>}
    {p.on_sale && size === "lg" && <span className="text-xs bg-[#F4E6C3] text-[#7A5410] px-2 py-0.5 uppercase tracking-wider">Vous économisez {eur(p.compare_at - p.price)}</span>}
  </div>
);

export const PromoBadge = ({ p }) => <span className="text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full bg-gradient-to-r from-[#9A6B12] via-[#C99A3A] to-[#9A6B12] text-white shadow-[0_4px_12px_-4px_rgba(138,99,20,.6)]" data-testid="promo-badge">Promo -{p.discount_pct}%</span>;
export const Badge = ({ children, dark, gold }) => <span className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full backdrop-blur-sm shadow-sm ${gold ? "bg-gradient-to-r from-[#8A6314] to-[#D8B66A] text-white" : dark ? "bg-black/90 text-white" : "bg-white/90 text-black"}`}>{children}</span>;
export const Stars = ({ v = 0, n }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`Note ${v} sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= Math.round(v) ? "fill-[var(--c-accent)] text-[var(--c-accent)]" : "text-black/20"} />)}
    {n != null && <span className="text-xs text-black/50 ml-1">({n})</span>}
  </span>
);

export const WishlistButton = ({ p, className = "" }) => {
  const { wish, toggleWish } = useStore();
  const on = wish.includes(p.id);
  return (
    <button aria-label={on ? "Retirer des favoris" : "Ajouter aux favoris"} aria-pressed={on} data-testid={`wishlist-btn-${p.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWish(p); }}
      className={`w-10 h-10 grid place-items-center rounded-full shadow-sm bg-white/85 backdrop-blur hover:bg-white transition-colors ${className}`}>
      <Heart size={18} className={on ? "fill-black" : ""} />
    </button>
  );
};

export const QuantitySelector = ({ value, onChange, max = 99 }) => (
  <div className="inline-flex items-center border border-black/20" data-testid="quantity-selector">
    <button aria-label="Diminuer" className="w-10 h-10 grid place-items-center hover:bg-black/5" onClick={() => onChange(value - 1)}><Minus size={14} /></button>
    <span className="w-8 text-center text-sm" aria-live="polite">{value}</span>
    <button aria-label="Augmenter" disabled={value >= max} className="w-10 h-10 grid place-items-center hover:bg-black/5 disabled:opacity-30" onClick={() => onChange(value + 1)}><Plus size={14} /></button>
  </div>
);

export const AddToCartButton = ({ p, variant, qty = 1, disabled, className = "", guard }) => {
  const { addItem } = useStore();
  return <button data-testid="add-to-cart-btn" disabled={disabled} onClick={() => { if (guard && !guard()) return; addItem(p, variant, qty); }} className={`btn-primary ${className}`}>{disabled ? "Indisponible" : "Ajouter au panier"}</button>;
};

export const ProductCard = ({ p, priority }) => {
  const { addItem } = useStore();
  const [a, b] = [p.images?.[0], p.images?.[1]];
  const single = !p.variants?.length;
  return (
    <div className="group relative" data-testid={`product-card-${p.slug}`}>
      <Link to={`/produit/${p.slug}`} className="block" onClick={() => track("select_item", { product_id: p.id }, { item_list_id: window.__LIST?.id, item_list_name: window.__LIST?.name, items: [gaItem(p, { list_id: window.__LIST?.id, list_name: window.__LIST?.name })] })}>
        <div className="relative aspect-[4/5] bg-[#efece4] overflow-hidden img-frame">
          {a ? <img src={img(a.url, "medium")} srcSet={srcset(a.url)} sizes="(min-width:1024px) 25vw, 50vw" decoding="async" fetchPriority={priority ? "high" : undefined} alt={a.alt || p.name} width="600" height="750" loading={priority ? "eager" : "lazy"} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" /> : null}
          {b && <img src={img(b.url, "medium")} srcSet={srcset(b.url)} sizes="(min-width:1024px) 25vw, 50vw" decoding="async" fetchPriority="low" alt="" width="600" height="750" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500" />}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1 max-w-[calc(100%-4.5rem)] max-sm:top-2 max-sm:left-2 max-sm:gap-0.5 max-sm:max-w-[calc(100%-3.25rem)] max-sm:[&>span]:!text-[7.5px] max-sm:[&>span]:!leading-none max-sm:[&>span]:!px-1.5 max-sm:[&>span]:!py-[3px] max-sm:[&>span]:!tracking-[0.06em] max-sm:[&>span]:!shadow-none">
            {p.on_sale && <PromoBadge p={p} />}
            {p.coming_soon && <Badge gold>À venir</Badge>}
            {!p.in_stock && !p.coming_soon && <Badge>Épuisé</Badge>}
            {p.in_stock && !p.preorder && p.available_stock > 0 && p.available_stock < 10 && <Badge dark>Plus que {p.available_stock} en stock</Badge>}
            {p.in_stock && p.is_new && !p.on_sale && !(p.available_stock < 10) && <Badge>Nouveau</Badge>}
            {(p.badges || []).filter((b) => b !== "À venir").slice(0, p.in_stock && p.is_new ? 1 : 2).map((b) => <Badge key={b}>{b}</Badge>)}
          </div>
        </div>
        <div className="pt-4 space-y-1">
          <h3 className="font-head text-xl leading-tight">{p.name}</h3>
          {p.review_count > 0 && <Stars v={p.rating_avg} n={p.review_count} />}
          <Price p={p} /><Klarna3x amount={p.price} />
        </div>
      </Link>
      <WishlistButton p={p} className="absolute top-3 right-3" />
      {single && p.in_stock && (
        <button onClick={() => addItem(p, null)} data-testid={`quick-add-${p.slug}`} className="hidden md:block absolute left-3 right-3 bottom-[5.5rem] bg-white/95 text-xs uppercase tracking-widest py-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-black hover:text-white">Ajouter au panier</button>
      )}
    </div>
  );
};

export const ProductGrid = ({ items, loading, cols = "grid-cols-2 lg:grid-cols-4", priorityN = 4 }) => (
  <div className={`grid ${cols} gap-x-4 sm:gap-x-8 gap-y-10 sm:gap-y-16`} data-testid="product-grid">
    {loading ? Array.from({ length: 8 }).map((_, i) => <div key={i}><div className="skeleton aspect-[4/5]" /><div className="skeleton h-5 mt-4 w-3/4" /><div className="skeleton h-4 mt-2 w-1/3" /></div>)
      : items.map((p, i) => <ProductCard key={p.id} p={p} priority={i < priorityN} />)}
  </div>
);

export const ProductCarousel = ({ items }) => (
  <div className="flex gap-4 sm:gap-8 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 sm:mx-0 sm:px-0" data-testid="product-carousel">
    {items.map((p) => <div key={p.id} className="snap-start shrink-0 w-[62%] sm:w-[28%] lg:w-[22%]"><ProductCard p={p} /></div>)}
  </div>
);

export const Breadcrumb = ({ trail }) => (
  <nav aria-label="Fil d'Ariane" className="text-xs text-black/50 flex flex-wrap items-center gap-1.5 py-4" data-testid="breadcrumb">
    <Link to="/" className="hover:text-black">Accueil</Link>
    {trail.map((t, i) => <span key={i} className="flex items-center gap-1.5"><ChevronRight size={11} />{t.to ? <Link to={t.to} className="hover:text-black">{t.label}</Link> : <span className="text-black">{t.label}</span>}</span>)}
  </nav>
);

export const breadcrumbLd = (trail) => ({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ name: "Accueil", to: "/" }, ...trail].map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.label || t.name, item: (window.__SITE || window.location.origin) + (t.to || window.location.pathname) })) });

const ICONS = { truck: Truck, shield: ShieldCheck, refresh: RefreshCw, gem: Gem };
export const TrustBadges = ({ compact }) => {
  const { settings } = useStore();
  const items = settings?.store?.trust_badges || [];
  return (
    <div className={`grid ${compact ? "grid-cols-1 gap-3" : "grid-cols-2 lg:grid-cols-4 gap-8"}`} data-testid="trust-badges">
      {items.map((b, i) => { const I = ICONS[b.icon] || Gem; return (
        <div key={i} className={`flex ${compact ? "flex-row items-center gap-3" : "flex-col items-start gap-3"}`}><I size={compact ? 18 : 26} strokeWidth={1.2} /><div><p className="text-sm font-medium">{b.title}</p><p className="text-xs text-black/60">{b.text}</p></div></div>); })}
    </div>
  );
};

export const FAQAccordion = ({ items }) => {
  const [open, setOpen] = useState(null);
  if (!items?.length) return null;
  return (
    <div className="divide-y border-y" data-testid="faq-accordion">
      {items.map((f, i) => (
        <div key={f.id || i}>
          <button aria-expanded={open === i} className="w-full flex justify-between items-center py-5 text-left font-medium" onClick={() => setOpen(open === i ? null : i)}>{f.question}<Plus size={16} className={`transition-transform ${open === i ? "rotate-45" : ""}`} /></button>
          {open === i && <div className="pb-5 text-black/70 leading-relaxed whitespace-pre-line">{f.answer}</div>}
        </div>))}
    </div>
  );
};
export const faqLd = (items) => items?.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: items.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) } : null;

export const NewsletterBox = ({ title = "Rejoignez la maison", text = "Nouveautés et inspirations, sans surcharge.", dark }) => {
  const [email, setEmail] = useState(""); const [done, setDone] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { const r = await api.post("/newsletter", { email, source: "footer" }); setDone(true); toast.success(r.data.message); track("newsletter_signup", { source: "footer" }, { method: "footer" }); track("generate_lead", {}, { lead_source: "newsletter" }); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <div className={dark ? "text-white" : ""} data-testid="newsletter-box">
      <h3 className="text-3xl font-head mb-2">{title}</h3><p className="text-sm opacity-70 mb-5">{text}</p>
      {done ? <p className="text-sm">Merci, à très vite.</p> : (
        <form onSubmit={submit} className="flex items-end gap-3 max-w-md"><input type="email" required aria-label="Votre email" placeholder="Votre email" value={email} onChange={(e) => setEmail(e.target.value)} className={`field ${dark ? "border-white/40 text-white placeholder:text-white/50" : ""}`} data-testid="newsletter-email-input" />
          <button className={dark ? "text-xs uppercase tracking-widest border border-white px-5 py-3 hover:bg-white hover:text-black transition-colors" : "btn-primary !px-5 !py-3"} data-testid="newsletter-submit-btn">S'inscrire</button></form>)}
    </div>
  );
};

export const ReviewCard = ({ r }) => (
  <article className="border p-6 bg-white" data-testid="review-card">
    <div className="flex items-center justify-between gap-2"><Stars v={r.rating} /><span className="inline-flex items-center gap-1 text-[11px] text-[#2f6b3f] font-medium whitespace-nowrap" data-testid="review-verified-badge"><BadgeCheck size={14} strokeWidth={1.8} />Avis vérifié</span></div>
    {r.title && <h4 className="font-medium mt-3">{r.title}</h4>}
    <p className="text-sm text-black/70 mt-2 leading-relaxed">{r.comment}</p>
    <p className="text-xs text-black/50 mt-4">{r.author}{r.verified && " · Achat confirmé"}{r.product_name && ` · ${r.product_name}`}</p>
    {r.responses?.map((x, i) => <div key={i} className="mt-4 pl-4 border-l-2 border-[var(--c-accent)] text-sm"><p className="text-xs uppercase tracking-wider mb-1">Réponse de la boutique</p>{x.text}</div>)}
  </article>
);

export const EmptyState = ({ icon: I = Gem, title, text, cta, to }) => (
  <div className="text-center py-24" data-testid="empty-state"><I size={40} strokeWidth={1} className="mx-auto mb-5 text-black/40" /><h2 className="text-3xl mb-2">{title}</h2><p className="text-black/60 mb-8">{text}</p>{cta && <Link to={to} className="btn-primary">{cta}</Link>}</div>
);

export const ShareButtons = ({ title }) => {
  const url = encodeURIComponent(window.location.href), t = encodeURIComponent(title || "");
  const A = ({ href, children, l }) => <a href={href} target="_blank" rel="noreferrer" aria-label={l} className="w-9 h-9 grid place-items-center border hover:bg-black hover:text-white transition-colors text-xs">{children}</a>;
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons"><Share2 size={14} className="mr-1" />
      <A href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} l="Facebook"><Facebook size={14} /></A>
      <A href={`https://pinterest.com/pin/create/button/?url=${url}&description=${t}`} l="Pinterest">P</A>
      <A href={`https://wa.me/?text=${t}%20${url}`} l="WhatsApp">W</A>
      <A href={`https://twitter.com/intent/tweet?url=${url}&text=${t}`} l="X">X</A>
      <button aria-label="Copier le lien" className="w-9 h-9 grid place-items-center border hover:bg-black hover:text-white transition-colors" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Lien copié"); }}><Link2 size={14} /></button>
    </div>
  );
};

export const SectionTitle = ({ eyebrow, title, link, linkLabel = "Tout voir" }) => (
  <div className="sec-title flex items-end justify-between mb-8 sm:mb-14"><div>{eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}<h2 className="text-3xl sm:text-4xl lg:text-5xl font-light">{title}</h2></div>{link && <Link to={link} className="font-head text-base italic tracking-[0.12em] border-b border-[var(--c-accent)] pb-1 hover:text-[#8A6314] transition-colors whitespace-nowrap">{linkLabel}</Link>}</div>
);

export function FreeShipBar({ dark = false }) {
  const { cart, settings: s } = useStore();
  const thr = cart?.free_shipping_threshold;
  if (!s.conversion.free_shipping_bar || !(thr > 0) || !cart?.lines?.length) return null;
  const rem = cart.free_shipping_remaining || 0, done = rem <= 0;
  const pct = Math.min(100, Math.max(0, ((cart.subtotal - cart.discount_total) / thr) * 100));
  return (
    <div className={`ship-bar ${done ? "is-done" : ""}`} data-testid="free-shipping-bar">
      <p className="ship-bar-msg" data-testid="free-shipping-msg">{done ? <><Check size={14} strokeWidth={2} /> Livraison offerte débloquée</> : <><Truck size={14} strokeWidth={1.5} /> Plus que <b>{eur(rem)}</b> pour la livraison offerte</>}</p>
      <div className="ship-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} data-testid="free-shipping-progress"><div className="ship-fill" style={{ width: `${pct}%` }} /></div>
      <div className="ship-scale"><span>0 €</span><span>{eur(thr)}</span></div>
    </div>
  );
}
